import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditStandard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStandard } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { assign } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  cardId: string
  standard: CardStandard
}

export const LESSON_STANDARDS_DELETE = {
  name: "LESSON_STANDARDS_DELETE",

  params: {
    cardStackId: "string",
    cardId: "string",
    standard: "object",
  },

  patches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.cardId,
    }).pull("embedded", "attributes.standards", { id: payload.standard.id }).patch
    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.cardId,
    }).addToSet(
      "embedded",
      "attributes.standards",
      JSON.parse(JSON.stringify(payload.standard))
    ).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let context = defaultLessonContext(payload, models)
    let deletedStandard = payload.standard
    let deletedStandardId = deletedStandard.id
    let deletedStandardCode = deletedStandard.code
    let deletedStandardJurisdiction = deletedStandard.jurisdiction
    let deletedStandardGrades = deletedStandard.grades
    let deletedStandardSubject = deletedStandard.subject
    let newContext = assign({}, context, {
      deletedStandardId,
      deletedStandardCode,
      deletedStandardJurisdiction,
      deletedStandardGrades,
      deletedStandardSubject,
    })
    let newEvent: LessonEditStandard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "standards",

      adverbial_verb: "removing",
      adverbial_object_type: "standard",
      adverbial_object_specifier: "lesson",
      adverbial_object_id: payload.standard.id,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Deleted a Standard in a Lesson",
      titleForAnalytics: "Standards - delete",
      description: `Removed a standard from "${context.cardStackTitle}"`,
      context: newContext,
      activity: defaultLessonActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_STANDARDS_DELETE
